import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import useFullPageLoader from "./useFullPageLoader";

const defaultApi = {
  registerSaveCallback:
    (callback: () => void, hasChanges?: boolean): (() => void) =>
    () => {},
  // triggerSaveAll: () => {},
  hasPendingSaves: false,
  hasActualChanges: false,
  triggerSaveAll: async (options?: {
    beforeSave?: void | (() => void);
    afterSave?: void | (() => void);
  }) => {},
  isAllValid: true,
  setValidForms: (validForms: Record<string, boolean>) => {},
  validForms: {},
};

export type SaveAllContextValue = typeof defaultApi & {
  validForms: Record<string, boolean>;
};

export const SaveAllContext = createContext<SaveAllContextValue>(defaultApi);

export const SaveAllProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const saveCallbacks = useRef<
    Array<{ callback: () => void; hasChanges: boolean }>
  >([]);
  const [callbackCount, setCallbackCount] = useState(0);
  const [changesCount, setChangesCount] = useState(0);
  const [, toggleLoader] = useFullPageLoader();
  const [validForms, setValidForms] = useState<Record<string, boolean>>({});

  const registerSaveCallback = useCallback((callback, hasChanges = true) => {
    const entry = { callback, hasChanges };
    saveCallbacks.current.push(entry);
    setCallbackCount(saveCallbacks.current.length);
    setChangesCount(saveCallbacks.current.filter((e) => e.hasChanges).length);

    return () => {
      saveCallbacks.current = saveCallbacks.current.filter(
        (cb) => cb !== entry,
      );
      setCallbackCount(saveCallbacks.current.length);
      setChangesCount(saveCallbacks.current.filter((e) => e.hasChanges).length);
    };
  }, []);

  const hasPendingSaves = useMemo(() => callbackCount > 0, [callbackCount]);
  const hasActualChanges = useMemo(() => changesCount > 0, [changesCount]);

  const triggerSaveAll = useCallback(
    async ({ beforeSave, afterSave } = {}) => {
      toggleLoader(true);
      if (beforeSave) {
        beforeSave();
      }

      const arrayOfPromises = saveCallbacks.current.map(
        (entry) =>
          new Promise((resolve, reject) => {
            try {
              resolve(entry.callback());
            } catch (error) {
              reject(error);
            }
          }),
      );

      await Promise.allSettled(arrayOfPromises).then(() => {
        if (afterSave) {
          afterSave();
        }
      });

      toggleLoader(false);
    },
    [toggleLoader],
  );

  const isAllValid = useMemo(
    () => Object.values(validForms).every((isValid) => isValid),
    [validForms],
  );

  const api = {
    hasPendingSaves,
    hasActualChanges,
    registerSaveCallback,
    triggerSaveAll,
    isAllValid,
    setValidForms,
    validForms,
  };

  return (
    <SaveAllContext.Provider value={api}>{children}</SaveAllContext.Provider>
  );
};
/*
 * This hook is used to register a list of save callbacks and trigger them all at once.
 * It is useful when you have multiple components that need to save their state at the same time.
 * e.g. A page with multiple forms that need to be saved together.
 */
export const useSaveAll = () => {
  const context = useContext(SaveAllContext);
  if (!context) {
    throw new Error("useSaveAll must be used within a SaveAllProvider");
  }
  return context;
};
